/** Currency symbols allowed in numbers, e.g. $65,903 or £100. They are ignored, so £100 is the number 100. */
export const CURRENCY_SYMBOLS = [
  "$",
  "£",
  "€",
  // yen
  "¥",
  // won
  "₩",
  // ruble
  "₽",
  // rupee
  "₹",
  // baht
  "฿",
  // dong
  "₫",
  // peso
  "₱",
  // lira
  "₺",
  // hryvnia
  "₴",
  // franc
  "₣",
];
export const CURRENCY_SYMBOLS_REGEX = new RegExp(`[${CURRENCY_SYMBOLS.join("")}]`, "g");

const DECLARED_DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
// No time zone: the rules engine ignores anything after the seconds, so an offset would be silently dropped
const DECLARED_DATE_TIME_REGEX = /^(\d{4}-\d{2}-\d{2})[\sT](?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;
const DECLARED_TIME_REGEX = /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;
// Plain decimal numbers once currency symbols, commas, spaces and a trailing % are removed (no exponents, hex or Infinity)
const DECLARED_NUMBER_REGEX = /^-?(?:\d+\.?\d*|\.\d+)$/;

/** True when the text is a YYYY-MM-DD date that exists, e.g. not 2024-02-30 (the rules engine rejects those) */
const isRealDate = (text: string) => {
  const match = DECLARED_DATE_REGEX.exec(text);
  if (!match) return false;
  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

/**
 * Normalises a declared attribute type. Older documents store display names such as "Date Time", so this matches
 * the way the portal normalises them. Returns undefined for "auto" and for types without a literal form.
 */
export const getDeclaredType = (type: string | undefined) => {
  const normalised = (type ?? "auto").toLowerCase().replace(/\s/g, "");
  switch (normalised) {
    case "text":
    case "string":
      return "text";
    case "number":
    case "currency":
    case "boolean":
    case "date":
    case "datetime":
      return normalised;
    case "time":
    case "timeofday":
      return "time";
    default:
      return undefined;
  }
};

export type DeclaredValueResult = { value: string | number | boolean } | { expected: string };

/**
 * Converts a static value written in a rule sheet cell or a rule document to the declared type of the attribute it is
 * compared with or assigned to. Returns undefined when the attribute has no declared type, so the caller keeps
 * inferring the type from the text. Returns `expected` (e.g. "a number") when the text is not a valid value of the type.
 */
export const parseDeclaredValue = (text: string, type: string | undefined): DeclaredValueResult | undefined => {
  const declaredType = getDeclaredType(type);
  switch (declaredType) {
    case "text":
      return { value: text };
    case "number":
    case "currency": {
      // Authors write amounts such as "$65,903" or "£100" for numbers as well as currency. A trailing % is ignored
      // rather than dividing by 100 (50% is 50), which is how rule documents have always read percentages.
      const cleaned = text.replace(CURRENCY_SYMBOLS_REGEX, "").replace(/[,\s]/g, "").replace(/%$/, "");
      if (!DECLARED_NUMBER_REGEX.test(cleaned)) {
        return { expected: declaredType === "number" ? "a number" : "a currency amount" };
      }
      return { value: Number(cleaned) };
    }
    case "boolean": {
      const lower = text.toLowerCase();
      if (lower !== "true" && lower !== "false") return { expected: "true or false" };
      return { value: lower === "true" };
    }
    case "date":
      if (!isRealDate(text)) return { expected: "a date (YYYY-MM-DD)" };
      return { value: text };
    case "datetime": {
      const datePart = DECLARED_DATE_TIME_REGEX.exec(text)?.[1] ?? text;
      if (!isRealDate(datePart)) return { expected: "a date time (YYYY-MM-DD HH:mm:ss)" };
      return { value: text };
    }
    case "time":
      if (!DECLARED_TIME_REGEX.test(text)) return { expected: "a time of day (HH:mm or HH:mm:ss)" };
      return { value: text.split(":").length < 3 ? `${text}:00` : text };
    default:
      return undefined;
  }
};
